import React, { useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Radio, Select, Spin, message } from 'antd';
import { getMenuDetail, type CreateMenuBody, type MenuItem } from './menuManageData';

const MENU_NO_PATTERN = /^[0-9]{21}$/;
const ROUTE_PATH_PATTERN = /^\/(?!\/)[^\s?#\\]*$/;

type MenuFormValues = {
  parentCode?: string;
  displayName: string;
  displayNameEn?: string;
  displayNameVi?: string;
  menuType: 'DIRECTORY' | 'MENU';
  routePath: string;
  componentKey?: string;
  sortOrder: number;
  status: 'ACTIVE' | 'DISABLED';
};

function optionalNameRule(label: string) {
  return {
    validator: (_: unknown, value: string) => {
      const text = value?.trim() ?? '';
      if (text.length > 128) return Promise.reject(new Error(`${label}最多 128 字`));
      return Promise.resolve();
    }
  };
}

type MenuFormModalProps = {
  open: boolean;
  menuNo: string | null;
  tree: MenuItem[];
  submitting: boolean;
  onClose: () => void;
  onSubmit: (body: CreateMenuBody, edit?: { menuNo: string; expectedConfigHash: string }) => void;
};

const createDefaults: MenuFormValues = {
  parentCode: undefined,
  displayName: '',
  displayNameEn: '',
  displayNameVi: '',
  menuType: 'DIRECTORY',
  routePath: '',
  componentKey: undefined,
  sortOrder: 0,
  status: 'ACTIVE'
};

function directoryOptions(
  nodes: MenuItem[],
  excludeNo?: string,
  level = 0
): Array<{ value: string; label: string }> {
  const options: Array<{ value: string; label: string }> = [];
  nodes.forEach((node) => {
    const code = node.menuNo?.trim() ?? '';
    if (excludeNo && code === excludeNo) return;
    const isDirectory = node.menuType === 'DIRECTORY' && MENU_NO_PATTERN.test(code);
    if (isDirectory) {
      const name = node.displayName?.trim() || code;
      options.push({ value: code, label: `${'\u3000'.repeat(level)}${name}` });
    }
    if (node.children?.length) {
      options.push(...directoryOptions(node.children, excludeNo, isDirectory ? level + 1 : level));
    }
  });
  return options;
}

export function MenuFormModal({ open, menuNo, tree, submitting, onClose, onSubmit }: MenuFormModalProps) {
  const [form] = Form.useForm<MenuFormValues>();
  const menuType = Form.useWatch('menuType', form);
  const [detailLoading, setDetailLoading] = useState(false);
  const [configHash, setConfigHash] = useState<string | undefined>();

  useEffect(() => {
    if (!open) {
      setConfigHash(undefined);
      setDetailLoading(false);
      return;
    }
    if (!menuNo) {
      setConfigHash(undefined);
      setDetailLoading(false);
      form.setFieldsValue(createDefaults);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setConfigHash(undefined);
    getMenuDetail(menuNo)
      .then((res) => {
        if (cancelled) return;
        if (res.code !== 200 || !res.content?.data) {
          if (res.code !== -1) message.error(res.message || '菜单详情加载失败');
          return;
        }
        const data = res.content.data;
        const parentCode = data.parentCode?.trim();
        form.setFieldsValue({
          parentCode: parentCode && MENU_NO_PATTERN.test(parentCode) ? parentCode : undefined,
          displayName: data.displayName ?? '',
          displayNameEn: data.displayNameEn ?? '',
          displayNameVi: data.displayNameVi ?? '',
          menuType: data.menuType === 'MENU' ? 'MENU' : 'DIRECTORY',
          routePath: data.routePath ?? '',
          componentKey: data.componentKey,
          sortOrder: data.sortOrder ?? 0,
          status: data.status === 'DISABLED' ? 'DISABLED' : 'ACTIVE'
        });
        const hash = res.content.configHash;
        setConfigHash(typeof hash === 'string' && hash ? hash : undefined);
      })
      .catch(() => {
        if (!cancelled) message.error('菜单详情加载失败');
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, menuNo, form]);

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const displayName = values.displayName.trim();
      const displayNameEn = values.displayNameEn?.trim();
      const displayNameVi = values.displayNameVi?.trim();
      const routePath = values.routePath.trim();
      const parentCode = values.parentCode?.trim();
      const body: CreateMenuBody = {
        displayName,
        menuType: values.menuType,
        routePath,
        sortOrder: values.sortOrder,
        status: values.status
      };
      if (displayNameEn) body.displayNameEn = displayNameEn;
      if (displayNameVi) body.displayNameVi = displayNameVi;
      if (parentCode) body.parentCode = parentCode;
      if (values.menuType === 'MENU') body.componentKey = values.componentKey?.trim();
      if (!menuNo) {
        onSubmit(body);
        return;
      }
      if (typeof configHash !== 'string' || !configHash) {
        message.warning('缺少配置校验值，请关闭后重新打开');
        return;
      }
      onSubmit(body, { menuNo, expectedConfigHash: configHash });
    } catch {
      // 表单校验未通过
    }
  };

  return (
    <Modal
      title={menuNo ? '编辑菜单' : '新增菜单'}
      open={open}
      onCancel={onClose}
      destroyOnClose
      centered
      width={760}
      footer={
        <div className='flex justify-end gap-2'>
          <Button className='rounded-lg' onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button
            type='primary'
            className='wb-btn-primary rounded-lg'
            loading={submitting}
            disabled={detailLoading}
            onClick={submit}
          >
            确认
          </Button>
        </div>
      }
    >
      {detailLoading ? (
        <div style={{ padding: '32px 0', textAlign: 'center' }}>
          <Spin />
        </div>
      ) : null}
      <Form
        form={form}
        layout='horizontal'
        labelCol={{ flex: '108px' }}
        wrapperCol={{ flex: 1 }}
        labelAlign='right'
        requiredMark
        className='pt-1 admin-menu-form'
        style={detailLoading ? { display: 'none' } : undefined}
      >
        <Form.Item name='parentCode' label='上级目录'>
          <Select
            allowClear
            placeholder='空为根节点'
            options={directoryOptions(tree, menuNo ?? undefined)}
            popupMatchSelectWidth={false}
            listHeight={280}
          />
        </Form.Item>
        <Form.Item name='status' label='菜单状态' rules={[{ required: true, message: '请选择状态' }]}>
          <Radio.Group>
            <Radio value='ACTIVE'>启用</Radio>
            <Radio value='DISABLED'>停用</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item label='菜单名称' required className='admin-menu-name-item'>
          <div className='admin-menu-name-grid'>
            <Form.Item
              name='displayName'
              rules={[
                {
                  validator: (_, value: string) => {
                    const text = value?.trim() ?? '';
                    if (!text) return Promise.reject(new Error('请输入中文名称'));
                    if (text.length > 128) return Promise.reject(new Error('中文名称最多 128 字'));
                    return Promise.resolve();
                  }
                }
              ]}
              style={{ marginBottom: 0 }}
            >
              <Input placeholder='中文' maxLength={128} />
            </Form.Item>
            <Form.Item name='displayNameEn' rules={[optionalNameRule('英文名称')]} style={{ marginBottom: 0 }}>
              <Input placeholder='英文' maxLength={128} />
            </Form.Item>
            <Form.Item name='displayNameVi' rules={[optionalNameRule('越南文名称')]} style={{ marginBottom: 0 }}>
              <Input placeholder='越南文' maxLength={128} />
            </Form.Item>
          </div>
        </Form.Item>
        <Form.Item name='menuType' label='节点类型' rules={[{ required: true, message: '请选择节点类型' }]}>
          <Radio.Group>
            <Radio value='DIRECTORY'>目录</Radio>
            <Radio value='MENU'>页面菜单</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name='routePath'
          label='路由路径'
          rules={[
            {
              validator: (_, value: string) => {
                const text = value?.trim() ?? '';
                if (!text) return Promise.reject(new Error('请输入路由路径'));
                if (text.length > 256) return Promise.reject(new Error('路由路径最多 256 字'));
                if (!ROUTE_PATH_PATTERN.test(text)) {
                  return Promise.reject(new Error('须以单个 / 开头，且不能包含空格、查询参数或片段'));
                }
                return Promise.resolve();
              }
            }
          ]}
        >
          <Input placeholder='如 /creator/talent_square' maxLength={256} />
        </Form.Item>
        {menuType === 'MENU' ? (
          <Form.Item
            name='componentKey'
            label='组件键'
            rules={[
              {
                validator: (_, value: string) => {
                  const text = value?.trim() ?? '';
                  if (!text) return Promise.reject(new Error('请输入页面组件键'));
                  if (text.length > 128) return Promise.reject(new Error('组件键最多 128 字'));
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input placeholder='已注册的页面组件键' maxLength={128} />
          </Form.Item>
        ) : null}
        <Form.Item name='sortOrder' label='排序' rules={[{ required: true, message: '请输入排序' }]}>
          <InputNumber precision={0} style={{ width: '100%' }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
